// frontend/src/hooks/useSpeechDictation.js
// Thin wrapper around the browser's SpeechRecognition API for single-field dictation.
// No WebSocket or backend involvement — purely browser-native speech-to-text.

import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const useSpeechDictation = () => {
  const [isListening,        setIsListening]        = useState(false);
  const [interimTranscript,  setInterimTranscript]  = useState('');

  const recognitionRef = useRef(null);
  const onResultRef    = useRef(null);   // callback(finalText) set each time start() is called
  const finalTextRef   = useRef('');     // accumulates final results during a session

  const isSupported = Boolean(SpeechRecognitionAPI);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback((onFinalResult) => {
    if (!isSupported) return;

    // Stop any in-flight session first
    recognitionRef.current?.abort();

    onResultRef.current = onFinalResult;
    finalTextRef.current = '';

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous      = false;
    recognition.interimResults  = true;
    recognition.lang            = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setInterimTranscript('');
    };

    recognition.onresult = (event) => {
      let interim = '';
      let finalSegment = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalSegment += text;
        } else {
          interim += text;
        }
      }
      if (finalSegment) finalTextRef.current += finalSegment;
      setInterimTranscript(interim || finalTextRef.current);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      const finalText = finalTextRef.current.trim();
      if (onResultRef.current && finalText) {
        onResultRef.current(finalText);
      }
    };

    recognition.onerror = (event) => {
      // 'no-speech' and 'aborted' are normal/expected — don't treat as errors
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.warn('[useSpeechDictation] Recognition error:', event.error);
      }
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isSupported]);

  // Cleanup on unmount
  useEffect(() => () => recognitionRef.current?.abort(), []);

  return { isSupported, isListening, interimTranscript, start, stop };
};

export default useSpeechDictation;
